import { Link, useRouter } from "@shared/client/navigation";
import { useState } from "react";
import { Check, ExternalLink, LoaderCircle, PauseCircle, PenLine, RotateCcw, X } from "lucide-react";
import {
  MESSAGE_STATUS_LABELS,
  OUTBOX_TABS,
  RANK_LABELS,
  type GuardIssue,
  type MessageQueueItem,
} from "@shared/lib/prospection/shared";
import { formatDate, formatDateTime } from "@shared/lib/format";
import { AdminChip, AdminEmpty, AdminMeta, AdminPanel } from "@shared/components/blocks";
import { Alert, AlertDescription } from "@shared/components/ui/alert";
import { Button } from "@shared/components/ui/button";
import { Input } from "@shared/components/ui/input";
import { Label } from "@shared/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@shared/components/ui/table";
import { Textarea } from "@shared/components/ui/textarea";
import { cn } from "@shared/lib/utils";

/**
 * File des e-mails de prospection. Chaque e-mail se lit tel qu'il sera reçu,
 * à côté de ce qui le justifie : le signal livré, sa source, et le verdict des
 * deux passes du garde-fou. On valide, on modifie, on retient avec un motif.
 */

async function call(id: string, body: Record<string, unknown>) {
  const response = await fetch(`/api/admin/prospection/messages/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error((data as { error?: string }).error || "Opération impossible.");
}

export function OutboxView({
  items,
  tab,
  counts,
  autoApprove,
}: {
  items: MessageQueueItem[];
  tab: string;
  counts: Record<string, number>;
  autoApprove: boolean;
}) {
  return (
    <>
      <AdminMeta
        note={
          autoApprove
            ? "Validation automatique : un e-mail qui passe le garde-fou part à son créneau."
            : "Validation automatique coupée : chaque e-mail attend votre clic."
        }
      >
        <span className="flex flex-wrap gap-x-4 gap-y-1.5">
          {OUTBOX_TABS.map((item) => (
            <Link
              className={cn("hover:text-foreground", item.key === tab && "font-medium text-foreground")}
              href={`/admin/prospection/envois?tab=${item.key}`}
              key={item.key}
            >
              {item.label}
              {counts[item.key] ? <span className="ml-1 text-subtle-foreground tabular-nums">{counts[item.key]}</span> : null}
            </Link>
          ))}
        </span>
      </AdminMeta>

      {items.length === 0 ? (
        <AdminPanel>
          <AdminEmpty>
            {tab === "to_review"
              ? "Rien à valider."
              : tab === "held"
                ? "Aucun e-mail retenu."
                : tab === "ready"
                  ? "Aucun e-mail n’attend son créneau."
                  : tab === "failed"
                    ? "Aucun échec."
                    : "Aucun e-mail envoyé pour l’instant."}
          </AdminEmpty>
        </AdminPanel>
      ) : tab === "sent" ? (
        <SentTable items={items} />
      ) : (
        <ul className="m-0 grid list-none gap-4 p-0">
          {items.map((item) => (
            <MessageCard item={item} key={`${item.id}-${item.updated_at}`} />
          ))}
        </ul>
      )}
    </>
  );
}

function SentTable({ items }: { items: MessageQueueItem[] }) {
  return (
    <AdminPanel flush>
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead className="pl-5">Envoyé</TableHead>
            <TableHead>Prospect</TableHead>
            <TableHead className="max-sm:pr-4">E-mail</TableHead>
            <TableHead className="max-sm:hidden">Objet</TableHead>
            <TableHead className="pr-5 max-sm:hidden">Boîte</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((item) => (
            <TableRow key={item.id}>
              <TableCell className="pl-5 whitespace-nowrap tabular-nums">{formatDateTime(item.sent_at)}</TableCell>
              <TableCell>
                {item.prospect ? (
                  <Link className="font-semibold hover:text-foreground" href={`/admin/prospection/${item.prospect.id}`}>
                    {item.prospect.name}
                  </Link>
                ) : (
                  "—"
                )}
              </TableCell>
              <TableCell className="whitespace-nowrap max-sm:pr-4">
                {item.kind === "reply" ? "Réponse" : RANK_LABELS[item.rank ?? 1]}
                {item.approved_auto && <small className="block text-[12px] text-muted-foreground">validé par le garde-fou</small>}
              </TableCell>
              <TableCell className="max-w-[320px] truncate max-sm:hidden">{item.subject ?? "—"}</TableCell>
              <TableCell className="pr-5 text-muted-foreground max-sm:hidden">{item.mailbox?.email ?? "—"}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </AdminPanel>
  );
}

function IssueList({ title, issues }: { title: string; issues: GuardIssue[] }) {
  if (issues.length === 0) return null;
  return (
    <div>
      <span className="text-[12px] font-medium text-muted-foreground">{title}</span>
      <ul className="m-0 mt-1 grid list-none gap-1 p-0">
        {issues.map((issue, index) => (
          <li className="text-[12.5px] leading-relaxed" key={`${issue.code}-${index}`}>
            <AdminChip tone={issue.severity === "major" ? "danger" : "neutral"}>
              {issue.severity === "major" ? "Bloquant" : "Mineur"}
            </AdminChip>{" "}
            {issue.message}
          </li>
        ))}
      </ul>
    </div>
  );
}

function MessageCard({ item }: { item: MessageQueueItem }) {
  const router = useRouter();
  const [editing, setEditing] = useState(false);
  const [subject, setSubject] = useState(item.subject ?? "");
  const [body, setBody] = useState(item.body);
  const [reason, setReason] = useState("");
  const [holding, setHolding] = useState(false);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");

  async function act(key: string, payload: Record<string, unknown>) {
    setBusy(key);
    setError("");
    try {
      await call(item.id, payload);
      router.refresh();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Opération impossible.");
    } finally {
      setBusy("");
    }
  }

  const isFirst = item.kind === "sequence" && item.rank === 1;
  const edits = editing ? { ...(isFirst ? { subject } : {}), body } : {};
  const label = item.kind === "reply" ? "Réponse au prospect" : RANK_LABELS[item.rank ?? 1];
  const guard = item.guard;

  return (
    <li>
      <AdminPanel
        action={
          <span className="text-[12px] text-muted-foreground">
            {MESSAGE_STATUS_LABELS[item.status]} · préparé le {formatDateTime(item.created_at)}
          </span>
        }
        title={`${item.prospect?.name ?? "Prospect supprimé"} — ${label}`}
      >
        <div className="grid gap-5 lg:grid-cols-[minmax(0,1.25fr)_minmax(0,1fr)]">
          <div className="grid content-start gap-3">
            <dl className="m-0 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-[12.5px]">
              <dt className="text-muted-foreground">À</dt>
              <dd className="m-0">
                {item.prospect?.contact_email ?? <span className="text-destructive">adresse manquante — ajoutez-la sur la fiche</span>}
              </dd>
              <dt className="text-muted-foreground">De</dt>
              <dd className="m-0">
                {item.mailbox ? `${item.mailbox.display_name} <${item.mailbox.email}>` : "la boîte attribuée à l’envoi"}
              </dd>
              {!editing && (
                <>
                  <dt className="text-muted-foreground">Objet</dt>
                  <dd className="m-0 font-medium">{item.subject || "réponse dans le fil"}</dd>
                </>
              )}
            </dl>

            {editing ? (
              <div className="grid gap-3">
                {isFirst && (
                  <div className="grid gap-2">
                    <Label htmlFor={`subject-${item.id}`}>Objet</Label>
                    <Input id={`subject-${item.id}`} maxLength={200} onChange={(event) => setSubject(event.target.value)} value={subject} />
                  </div>
                )}
                <div className="grid gap-2">
                  <Label htmlFor={`body-${item.id}`}>Corps (la signature et le pied s’ajoutent à l’envoi)</Label>
                  <Textarea
                    className="text-[13px] leading-relaxed"
                    id={`body-${item.id}`}
                    maxLength={8000}
                    onChange={(event) => setBody(event.target.value)}
                    rows={12}
                    value={body}
                  />
                </div>
              </div>
            ) : (
              <pre className="m-0 rounded-lg border bg-muted/30 p-4 font-sans text-[13px] leading-relaxed whitespace-pre-wrap">
                {item.preview}
              </pre>
            )}
          </div>

          <div className="grid content-start gap-4 text-[12.5px] leading-relaxed">
            {item.prospect && (
              <div>
                <span className="text-[12px] font-medium text-muted-foreground">Destinataire</span>
                <p className="m-0 mt-1">
                  <Link className="font-medium hover:underline" href={`/admin/prospection/${item.prospect.id}`}>
                    {item.prospect.name}
                  </Link>
                  {item.prospect.contact_name ? ` — ${item.prospect.contact_name}` : ""}
                </p>
                {item.prospect.offer_name && <p className="m-0 text-muted-foreground">Vend : {item.prospect.offer_name}</p>}
                {item.prospect.priority_segment && (
                  <p className="m-0 text-muted-foreground">Vise : {item.prospect.priority_segment}</p>
                )}
              </div>
            )}

            {item.lead && (
              <div>
                <span className="text-[12px] font-medium text-muted-foreground">Signal livré</span>
                <p className="m-0 mt-1 font-medium">{item.lead.organization}</p>
                {item.lead.market_clue && <p className="m-0">{item.lead.market_clue}</p>}
                <p className="m-0 flex flex-wrap gap-x-3 text-muted-foreground">
                  {item.lead.observed_on && <span>Observé le {/^\d{4}-\d{2}-\d{2}$/.test(item.lead.observed_on) ? formatDate(item.lead.observed_on) : item.lead.observed_on}</span>}
                  {item.lead.source_link && (
                    <a className="inline-flex items-center gap-1 text-primary hover:underline" href={item.lead.source_link} rel="noreferrer noopener" target="_blank">
                      {item.lead.source_name || "Source"} <ExternalLink className="size-3" />
                    </a>
                  )}
                </p>
              </div>
            )}

            {guard ? (
              <div className="grid gap-2.5">
                <span className="text-[12px] font-medium text-muted-foreground">
                  Garde-fou :{" "}
                  <AdminChip tone={guard.verdict === "pass" ? "active" : "danger"}>
                    {guard.verdict === "pass" ? "passe" : "retenu"}
                  </AdminChip>
                </span>
                {guard.reviewer_failure && <p className="m-0 text-destructive">Relecteur indisponible : {guard.reviewer_failure}</p>}
                <IssueList issues={guard.deterministic} title="Contrôles du code" />
                <IssueList issues={guard.model} title="Second modèle" />
                {guard.deterministic.length + guard.model.length === 0 && !guard.reviewer_failure && (
                  <p className="m-0 text-muted-foreground">Aucune remarque.</p>
                )}
              </div>
            ) : item.kind === "reply" ? (
              <p className="m-0 text-muted-foreground">Réponse préparée par la machine : relisez-la avant de l’envoyer.</p>
            ) : null}

            {/* Un e-mail retenu par le garde-fou a déjà son détail ci-dessus ; ici, le motif d'une personne. */}
            {item.hold_reason && item.status === "held" && (!guard || guard.verdict === "pass") && (
              <p className="m-0">Motif : {item.hold_reason}</p>
            )}
            {item.error && <p className="m-0 text-destructive">{item.error}</p>}
            {item.angle && <p className="m-0 text-muted-foreground">Angle : {item.angle}</p>}
            {item.edited_at && <p className="m-0 text-muted-foreground">Modifié le {formatDateTime(item.edited_at)}</p>}
          </div>
        </div>

        {error && (
          <Alert className="mt-4" role="alert" variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {holding && (
          <form
            className="mt-4 flex flex-wrap items-center gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              act("hold", { action: "hold", reason });
            }}
          >
            <Input
              className="h-9 max-w-[420px]"
              onChange={(event) => setReason(event.target.value)}
              placeholder="Motif : ton, signal faux, trop long…"
              value={reason}
            />
            <Button disabled={busy === "hold"} size="sm" type="submit" variant="outline">
              {busy === "hold" ? <LoaderCircle className="animate-spin" /> : <PauseCircle />} Retenir
            </Button>
          </form>
        )}

        <div className="mt-4 flex flex-wrap gap-2">
          {(item.status === "to_review" || item.status === "held") && (
            <Button disabled={busy === "approve"} onClick={() => act("approve", { action: "approve", ...edits })} size="sm" type="button">
              {busy === "approve" ? <LoaderCircle className="animate-spin" /> : <Check />}
              {editing ? "Enregistrer et valider" : item.status === "held" ? "Valider quand même" : "Valider"}
            </Button>
          )}
          {item.status === "ready" && editing && (
            <Button disabled={busy === "save"} onClick={() => act("save", { action: "save", ...edits })} size="sm" type="button">
              {busy === "save" ? <LoaderCircle className="animate-spin" /> : <Check />} Enregistrer
            </Button>
          )}
          {item.status !== "failed" && (
            <Button onClick={() => setEditing((value) => !value)} size="sm" type="button" variant="outline">
              <PenLine /> {editing ? "Annuler la modification" : "Modifier"}
            </Button>
          )}
          {(item.status === "to_review" || item.status === "held") && editing && (
            <Button disabled={busy === "save"} onClick={() => act("save", { action: "save", ...edits })} size="sm" type="button" variant="outline">
              Enregistrer sans valider
            </Button>
          )}
          {(item.status === "to_review" || item.status === "ready") && !holding && (
            <Button onClick={() => setHolding(true)} size="sm" type="button" variant="outline">
              <PauseCircle /> Retenir
            </Button>
          )}
          {item.status === "failed" && (
            <Button
              disabled={busy === "retry"}
              onClick={() => {
                if (!confirm("Vérifiez dans Gmail que cet e-mail n’est pas déjà parti. Le renvoyer ?")) return;
                act("retry", { action: "retry" });
              }}
              size="sm"
              type="button"
            >
              {busy === "retry" ? <LoaderCircle className="animate-spin" /> : <RotateCcw />} Renvoyer
            </Button>
          )}
          <Button
            className="text-muted-foreground hover:text-destructive"
            disabled={busy === "cancel"}
            onClick={() => {
              if (!confirm("Abandonner cet e-mail ? Il ne partira pas.")) return;
              act("cancel", { action: "cancel" });
            }}
            size="sm"
            type="button"
            variant="ghost"
          >
            <X /> Abandonner
          </Button>
        </div>
      </AdminPanel>
    </li>
  );
}
